import { useEffect, useState, useCallback } from 'react';
import { subscriptionsAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inicializar blocked a partir do localStorage para resposta imediata
  const [blocked, setBlocked] = useState(() => {
    return localStorage.getItem('subscriptionBlocked') === 'true';
  });
  const [warning, setWarning] = useState(null);

  const checkSubscription = useCallback(async () => {
    // Verificar se há usuário e se é adm@revicont.dev ou admin sem companyId (exceção)
    const isMasterAdmin = user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId);
    if (isMasterAdmin) {
      setLoading(false);
      setSubscription(null);
      setBlocked(false);
      return;
    }

    // Verificar se há token de autenticação
    const token = localStorage.getItem('token');
    if (!token || !user) {
      setLoading(false);
      setSubscription(null);
      setBlocked(false);
      return;
    }

    setLoading(true);
    try {
      // Adicionar timeout para evitar loading infinito
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout ao verificar assinatura')), 5000)
      );
      
      const data = await Promise.race([
        subscriptionsAPI.getMySubscription(),
        timeoutPromise
      ]);
      setSubscription(data);

      // Verificar se está bloqueado
      const now = new Date();
      
      // Trial expirado (mais de 14 dias)
      if (data.status === 'trial' && data.trialEndsAt) {
        const trialEnded = new Date(data.trialEndsAt) < now;
        if (trialEnded) {
          setBlocked(true);
          setLoading(false);
          return;
        }
      }
      
      // Assinatura suspensa ou cancelada - SEMPRE bloquear
      // O ProtectedRoute permitirá acesso apenas a /billing
      if (data.status === 'suspended' || data.status === 'canceled') {
        setBlocked(true);
        setLoading(false);
        return;
      }
      
      // Pagamento vencido há mais de 7 dias
      if (data.nextPaymentDate) {
        const paymentDate = new Date(data.nextPaymentDate);
        const daysOverdue = Math.floor((now.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysOverdue > 7) {
          setBlocked(true);
          setLoading(false);
          return;
        }
      }
      
      setBlocked(false);

      // Verificar se está próximo do vencimento
      if (data.isExpiring && data.daysUntilExpiry > 0) {
        setWarning({
          type: 'expiring',
          message: `Sua assinatura vence em ${data.daysUntilExpiry} dia(s)!`,
          daysLeft: data.daysUntilExpiry
        });
      } else if (data.isExpired && data.status === 'active') {
        setWarning({
          type: 'expired',
          message: 'Sua assinatura está vencida! Regularize o pagamento.',
          daysOverdue: Math.abs(data.daysUntilExpiry)
        });
      } else {
        setWarning(null);
      }
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error);
      
      // Se for timeout, não bloquear e permitir acesso
      if (error.message === 'Timeout ao verificar assinatura') {
        console.warn('Timeout ao verificar assinatura - permitindo acesso');
        setBlocked(false);
        setSubscription(null);
        setLoading(false);
        return;
      }
      
      // Se erro 403 e for relacionado a assinatura bloqueada/suspensa, ainda retornar os dados se disponíveis
      if (error.response?.status === 403 && error.response?.data?.status) {
        // A API retornou que está bloqueado, mas ainda retornou dados da assinatura
        const blockedSubscription = error.response.data;
        if (blockedSubscription.status) {
          setSubscription(blockedSubscription);
          setBlocked(true);
          setLoading(false);
          return;
        }
      }
      
      // Se der erro 404, usuário não tem empresa ou assinatura
      if (error.response?.status === 404) {
        setBlocked(false);
        setSubscription(null);
      } else if (error.response?.status === 403) {
        // Erro 403 - sempre marcar como bloqueado se relacionado a assinatura
        // O ProtectedRoute permitirá acesso apenas a /billing
        const errorData = error.response.data;
        if (errorData.status || errorData.subscription || 
            errorData.code === 'SUBSCRIPTION_SUSPENDED' || 
            errorData.code === 'COMPANY_SUSPENDED' || 
            errorData.code === 'COMPANY_BLOCKED' ||
            errorData.code === 'TRIAL_EXPIRED') {
          // Se tiver dados da assinatura no erro, usar
          if (errorData.subscription || errorData.status) {
            setSubscription(errorData.subscription || errorData);
          }
          setBlocked(true);
        } else {
          // Outros tipos de 403 - não bloquear
          setBlocked(false);
          setSubscription(null);
        }
      } else {
        // Outros erros - não bloquear, apenas não ter assinatura
        setBlocked(false);
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user]); // Dependência do user

  useEffect(() => {
    // Verificar se é adm@revicont.dev ou admin sem companyId (exceção)
    const isMasterAdmin = user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId);
    if (isMasterAdmin) {
      setLoading(false);
      setSubscription(null);
      setBlocked(false);
      setWarning(null);
      return;
    }

    // Só verificar se houver usuário e token
    const token = localStorage.getItem('token');
    if (!token || !user) {
      setLoading(false);
      setSubscription(null);
      setBlocked(false);
      setWarning(null);
      return;
    }

    // Chamar apenas uma vez quando o componente montar ou user mudar
    checkSubscription();

    // Verificar a cada 2 minutos (apenas se não for master admin)
    const interval = setInterval(() => {
      if (!isMasterAdmin && token && user) {
        checkSubscription();
      }
    }, 2 * 60 * 1000);

    // Listener para atualizar quando houver mudanças
    const handleSubscriptionUpdate = () => {
      if (!isMasterAdmin && token && user) {
        checkSubscription();
      }
    };

    // Listener para quando uma API retorna 403 (bloqueio)
    const handleBlockedAccess = (event) => {
      const errorData = event.detail;
      if (errorData?.code === 'SUBSCRIPTION_SUSPENDED' || 
          errorData?.code === 'COMPANY_SUSPENDED' || 
          errorData?.code === 'COMPANY_BLOCKED' ||
          errorData?.code === 'TRIAL_EXPIRED') {
        setBlocked(true);
        if (errorData.subscription || errorData.status) {
          setSubscription(errorData.subscription || errorData);
        }
        localStorage.setItem('subscriptionBlocked', 'true');
      }
    };

    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    window.addEventListener('blockedAccess', handleBlockedAccess);

    return () => {
      clearInterval(interval);
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
      window.removeEventListener('blockedAccess', handleBlockedAccess);
    };
  }, [user, checkSubscription]); // Incluir checkSubscription para garantir que use a versão mais recente

  const refresh = useCallback(() => {
    checkSubscription();
  }, [checkSubscription]);

  return { subscription, loading, blocked, warning, refresh };
}


